import type { ReactNode } from "react";
import { Alert, Box, Button, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { match } from "ts-pattern";
import type { Promisable } from "type-fest";

import {
  FULL_FILTERS,
  fullAssessmentOf,
  type Assessment,
  type FullAssessmentView,
  type FullFilter,
  type FullItem,
} from "@thalamos/haven-common";

import { apiHooks } from "../../api/index.js";
import { FullAssessment } from "../../components/FullAssessment/FullAssessment.js";
import { useUser } from "../../Context/UserContext.js";
import {
  createRoute,
  FULL_VIEW_PARAMETERS,
  type FullView,
} from "../../routing/createRoute.js";
import { useAssessment, useInstrumentForm } from "./AssessmentContext.js";
import {
  assessmentPdfDownloadOf,
  type PdfDownloadOutcome,
} from "./assessmentPdfDownload.js";
import { AssessmentViewHeader } from "./AssessmentViewHeader.js";
import { familyOfName } from "./comparisonCandidates.js";
import { fullHistoryHrefOf } from "./fullHistoryHrefOf.js";
import { latestSubmission } from "./latestSubmission.js";
import { factsOf, type SummaryFacts } from "./summaryFacts.js";

/** What the page shows where the answers are: them, or why they are not shown. */
export type FullBody =
  | { kind: "loading" }
  | { kind: "unavailable" }
  | { kind: "not_submitted" }
  | { kind: "shown"; view: FullAssessmentView };

/** How the answers are laid out, every part of it read from the address. */
export type FullLayout = Required<FullView>;

const filterOf = (value: string | null): FullFilter =>
  FULL_FILTERS.find((filter) => filter === value) ?? "all";

/** The layout an address names, each part it leaves out at its default. */
export const layoutOf = (query: URLSearchParams): FullLayout => ({
  filter: filterOf(query.get(FULL_VIEW_PARAMETERS.filter)),
  fullText: query.get(FULL_VIEW_PARAMETERS.fullText) === "full",
  open: query.getAll(FULL_VIEW_PARAMETERS.open),
});

/**
 * Every answer of an assessment's latest submission, at an address of its own
 * that holds how they are laid out, so a link opens the same view.
 */
export const AssessmentFullPage = () => {
  const { assessment } = useAssessment();

  return assessment === undefined ? null : <FullOf assessment={assessment} />;
};

const FullOf = ({ assessment }: { assessment: Assessment }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const layout = layoutOf(searchParams);
  const { user } = useUser();
  const { loading: form, retry } = useInstrumentForm(assessment);
  const [{ data: involvements, response }] = apiHooks.involvement.forPerson(
    assessment.personId,
  );
  // A 4xx body resolves onto `data`, so it is only taken from a 200.
  const known = response?.status === 200 ? involvements : undefined;
  const submission = latestSubmission(assessment);
  const family = familyOfName(assessment.instrument.name);

  const body = (): FullBody => {
    if (submission === undefined) {
      return { kind: "not_submitted" };
    }

    return match(form)
      .with({ kind: "loading" }, (): FullBody => ({ kind: "loading" }))
      .with({ kind: "failed" }, (): FullBody => ({ kind: "unavailable" }))
      .with({ kind: "ready" }, ({ loaded }): FullBody => ({
        kind: "shown",
        view: fullAssessmentOf({
          schema: loaded.schema,
          form: loaded.form,
          responses: submission.responses,
        }),
      }))
      .exhaustive();
  };

  const facts =
    submission === undefined
      ? undefined
      : factsOf({
          current: { assessment, submission },
          involvements: known,
          viewer: user,
          schema: form.kind === "ready" ? form.loaded.schema : undefined,
        });

  return (
    <AssessmentFullPageContent
      facts={facts}
      body={body()}
      onDownloadPdf={assessmentPdfDownloadOf(assessment)}
      layout={layout}
      summaryHref={createRoute.assessmentSummary(assessment.id)}
      historyHref={fullHistoryHrefOf({
        assessment,
        involvements: known,
        family,
      })}
      historyHrefOf={({ item }) =>
        createRoute.fullHistory(assessment.personId, {
          family,
          back: assessment.id,
          items: "all",
          item,
        })
      }
      onLayout={(chosen) => {
        // A change of layout replaces the entry: it is the same view.
        navigate(createRoute.assessmentFull(assessment.id, chosen), {
          replace: true,
        });
      }}
      onRetry={retry}
    />
  );
};

const BodyShown = ({
  body,
  layout,
  historyHrefOf,
  onLayout,
  onRetry,
}: {
  body: FullBody;
  layout: FullLayout;
  historyHrefOf?: ((item: FullItem) => string) | undefined;
  onLayout: (layout: FullView) => Promisable<void>;
  onRetry: () => Promisable<void>;
}): ReactNode => {
  const { t } = useTranslation();

  return match(body)
    .with({ kind: "loading" }, () => (
      <Typography color="text.secondary">
        {t("assessment.full.loading")}
      </Typography>
    ))
    .with({ kind: "unavailable" }, () => (
      <Alert
        severity="warning"
        action={
          <Button color="inherit" size="small" onClick={() => void onRetry()}>
            {t("assessment.full.retry")}
          </Button>
        }
      >
        {t("assessment.full.formUnavailable")}
      </Alert>
    ))
    .with({ kind: "not_submitted" }, () => (
      <Alert severity="info">{t("assessment.full.notSubmitted")}</Alert>
    ))
    .with({ kind: "shown" }, ({ view }) => (
      <FullAssessment
        view={view}
        filter={layout.filter}
        fullText={layout.fullText}
        open={new Set(layout.open)}
        onFilter={(filter) => onLayout({ ...layout, filter })}
        onFullText={(fullText) => onLayout({ ...layout, fullText })}
        onOpen={(open) => onLayout({ ...layout, open })}
        historyHrefOf={historyHrefOf}
      />
    ))
    .exhaustive();
};

/**
 * The full view's rendering, with everything it shows injected so a story or
 * a test can show any state of it without an API client.
 */
export const AssessmentFullPageContent = ({
  facts,
  body,
  layout,
  summaryHref,
  historyHref,
  historyHrefOf,
  onDownloadPdf,
  onLayout,
  onRetry,
}: {
  facts?: SummaryFacts | undefined;
  body: FullBody;
  layout: FullLayout;
  summaryHref: string;
  /** Absent where the history is not offered. */
  historyHref?: string | undefined;
  historyHrefOf?: ((item: FullItem) => string) | undefined;
  /** Absent where the assessment is not offered as a PDF. */
  onDownloadPdf?: (() => Promisable<PdfDownloadOutcome>) | undefined;
  onLayout: (layout: FullView) => Promisable<void>;
  onRetry: () => Promisable<void>;
}) => (
  <Box>
    <AssessmentViewHeader
      facts={facts}
      view={{ kind: "full", summaryHref }}
      historyHref={historyHref}
      onDownloadPdf={onDownloadPdf}
    />
    <Box sx={{ p: 2, bgcolor: "background.subtle" }}>
      <BodyShown
        body={body}
        layout={layout}
        historyHrefOf={historyHrefOf}
        onLayout={onLayout}
        onRetry={onRetry}
      />
    </Box>
  </Box>
);
